import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import FilesClient from 'src/clients/FilesClient';
import {
  alertSnackbar,
  completeProgress,
  resetProgressValue,
  setProgressValue,
} from 'src/store/ui/actions';

const POLL_INTERVAL_MS = 500;

interface UseProgressBatchDeleteOptions {
  getProgress?: typeof FilesClient.getBatchDeleteProgress;
  onSuccess?: () => void;
}

export const useProgressBatchDelete = ({
  getProgress = FilesClient.getBatchDeleteProgress,
  onSuccess,
}: UseProgressBatchDeleteOptions = {}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Tracks whether the hook is still mounted so the recursive setTimeout chain
  // can short-circuit instead of dispatching after unmount.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updateBatchDeleteProgress = async (
    deleteJobKey: string,
    totalCount: number,
  ) => {
    if (!isMountedRef.current) return;
    let progress: Awaited<ReturnType<typeof getProgress>>;
    try {
      progress = await getProgress(deleteJobKey);
    } catch {
      if (!isMountedRef.current) return;
      dispatch(completeProgress());
      dispatch(
        alertSnackbar({
          errorMessage: t(($) => $.page_files.batch_actions.delete_failed),
        }),
      );
      return;
    }
    if (!isMountedRef.current) return;

    if (progress.status === 'FAILED') {
      dispatch(completeProgress());
      dispatch(
        alertSnackbar({
          errorMessage:
            progress.additionalFields?.failureReason ||
            t(($) => $.page_files.batch_actions.delete_failed),
        }),
      );
      return;
    }

    if (progress.status === 'ENABLED') {
      const deletedFiles = progress.additionalFields?.deletedFiles ?? 0;
      const totalFiles = progress.additionalFields?.totalFiles ?? totalCount;
      const failedFileIds: string[] =
        progress.additionalFields?.failedFileIds ?? [];

      dispatch(completeProgress());

      onSuccess?.();

      if (failedFileIds.length === 0) {
        dispatch(
          alertSnackbar({
            successMessage: t(
              ($) => $.page_files.batch_actions.delete_success,
              { count: deletedFiles },
            ),
          }),
        );
      } else {
        dispatch(
          alertSnackbar({
            successMessage: t(
              ($) => $.page_files.batch_actions.delete_partial_failure,
              { deletedCount: deletedFiles, totalCount: totalFiles },
            ),
          }),
        );
      }
      return;
    }

    // While the worker is still in its expansion phase, totalFiles is 0 —
    // avoid dividing by zero so the bar sits at 0% instead of flashing NaN.
    const totalFiles = progress.additionalFields?.totalFiles ?? 0;
    const deletedFiles = progress.additionalFields?.deletedFiles ?? 0;
    const pct =
      totalFiles > 0 ? Math.round((deletedFiles / totalFiles) * 100) : 0;
    dispatch(setProgressValue(pct));
    setTimeout(() => {
      updateBatchDeleteProgress(deleteJobKey, totalCount);
    }, POLL_INTERVAL_MS);
  };

  const startBatchDeleteProgress = (deleteJobKey: string, count: number) => {
    if (deleteJobKey === '') {
      return;
    }
    dispatch(resetProgressValue());
    dispatch(
      alertSnackbar(
        {
          progressMessage: t(
            ($) => $.page_files.batch_actions.delete_in_progress,
            { count },
          ),
        },
        true,
      ),
    );
    updateBatchDeleteProgress(deleteJobKey, count);
  };

  return startBatchDeleteProgress;
};
